
import React, { useState, useEffect } from 'react';
import { Send, MessageSquare, ThumbsUp, Reply, Loader2, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { commentService, Comment } from '../../services/commentService';

interface CommentSectionProps {
  articleId: string;
  className?: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({
  articleId,
  className = '',
}) => {
  const { state } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [likingCommentId, setLikingCommentId] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [commentPendingDelete, setCommentPendingDelete] = useState<string | null>(null);

  // Fetch comments on mount
  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        const fetchedComments = await commentService.getComments(articleId, null); // null = top-level only

        // Organize comments into tree structure (parent + replies)
        const topLevelComments = fetchedComments.filter(c => !c.parentId);
        const repliesMap = new Map<string, Comment[]>();

        fetchedComments.forEach(comment => {
          if (comment.parentId) {
            if (!repliesMap.has(comment.parentId)) {
              repliesMap.set(comment.parentId, []);
            }
            repliesMap.get(comment.parentId)!.push(comment);
          }
        });

        // Attach replies to their parents
        let commentsWithReplies = topLevelComments.map(comment => ({
          ...comment,
          replies: repliesMap.get(comment.id) || [],
        }));

        if (state.isAuthenticated) {
          const allIds = fetchedComments.map(comment => comment.id);
          const statuses = await commentService.getLikeStatuses(allIds);
          commentsWithReplies = commentsWithReplies.map(comment => ({
            ...comment,
            isLiked: statuses[comment.id] === true,
            replies: (comment.replies || []).map(reply => ({
              ...reply,
              isLiked: statuses[reply.id] === true,
            })),
          }));
        }

        setComments(commentsWithReplies);
      } catch (error: any) {
        console.error('Failed to fetch comments:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [articleId, state.isAuthenticated]);

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !state.isAuthenticated || isSubmitting) return;

    try {
      setIsSubmitting(true);

      const comment = await commentService.create({
        contentPageId: articleId,
        content: newComment,
      });

      // Add to comments list
      setComments([...comments, { ...comment, replies: [] }]);
      setNewComment('');
    } catch (error: any) {
      console.error('Failed to submit comment:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      // TODO: Show error toast/notification to user
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReply = async (parentId: string) => {
    if (!replyContent.trim() || !state.isAuthenticated || isSubmitting) return;

    try {
      setIsSubmitting(true);

      const reply = await commentService.create({
        contentPageId: articleId,
        content: replyContent,
        parentId,
      });

      // Update comments to add reply
      const updateComments = (commentsList: Comment[]): Comment[] => {
        return commentsList.map(comment => {
          if (comment.id === parentId) {
            return {
              ...comment,
              replies: [...(comment.replies || []), reply],
            };
          }
          if (comment.replies && comment.replies.length > 0) {
            return {
              ...comment,
              replies: updateComments(comment.replies),
            };
          }
          return comment;
        });
      };

      setComments(updateComments(comments));
      setReplyContent('');
      setReplyingTo(null);
    } catch (error: any) {
      console.error('Failed to submit reply:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      // TODO: Show error toast/notification to user
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = async (commentId: string) => {
    if (!state.isAuthenticated || likingCommentId === commentId) return;

    try {
      setLikingCommentId(commentId);
      const currentComment = (list: Comment[]): Comment | undefined => {
        for (const item of list) {
          if (item.id === commentId) return item;
          const nested = currentComment(item.replies || []);
          if (nested) return nested;
        }
        return undefined;
      };
      const wasLiked = currentComment(comments)?.isLiked === true;
      const result = wasLiked
        ? await commentService.unlike(commentId)
        : await commentService.like(commentId);

      // Update local state
      const updateComments = (commentsList: Comment[]): Comment[] => {
        return commentsList.map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              likes: result.likes,
              isLiked: result.liked,
            };
          }
          if (comment.replies && comment.replies.length > 0) {
            return {
              ...comment,
              replies: updateComments(comment.replies),
            };
          }
          return comment;
        });
      };

      setComments(updateComments(comments));
    } catch (error: any) {
      console.error('Failed to like comment:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
    } finally {
      setLikingCommentId(null);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!state.isAuthenticated || deletingCommentId) return;

    try {
      setDeletingCommentId(commentId);
      await commentService.delete(commentId);
      const removeComment = (items: Comment[]): Comment[] => items
        .filter(comment => comment.id !== commentId)
        .map(comment => ({
          ...comment,
          replies: removeComment(comment.replies || []),
        }));
      setComments(current => removeComment(current));
      if (replyingTo === commentId) setReplyingTo(null);
    } catch (error) {
      console.error('Failed to delete comment:', error);
    } finally {
      setDeletingCommentId(null);
      setCommentPendingDelete(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const renderComment = (comment: Comment, isReply = false) => (
    <div key={comment.id} className={isReply ? 'ml-8 mt-4' : ''}>
      <div className="flex gap-3">
        <div className="flex-shrink-0">
          {comment.author.avatar ? (
            <img
              src={comment.author.avatar}
              alt={comment.author.fullName}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="comment-author-avatar w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold text-sm">
              {comment.author.fullName.charAt(0)}
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                  {comment.author.fullName}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {formatDate(comment.createdAt)}
                  {comment.isEdited && <span className="ml-2">(edited)</span>}
                </p>
              </div>
            </div>
            <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">
              {comment.content}
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleLike(comment.id)}
                className="article-comment-action flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                disabled={!state.isAuthenticated || likingCommentId === comment.id}
                aria-pressed={comment.isLiked === true}
              >
                <ThumbsUp className={`w-4 h-4 ${comment.isLiked ? 'fill-current' : ''}`} />
                <span>{comment.likes}</span>
              </button>
              {!isReply && state.isAuthenticated && (
                <button
                  onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                  className="article-comment-action flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  <Reply className="w-4 h-4" />
                  <span>Reply</span>
                </button>
              )}
              {state.user?.id === comment.author.id && (
                <button
                  type="button"
                  onClick={() => setCommentPendingDelete(comment.id)}
                  disabled={deletingCommentId === comment.id}
                  className="article-comment-delete flex items-center gap-1 text-xs transition-colors"
                  aria-label="Delete comment"
                  title="Delete comment"
                >
                  {deletingCommentId === comment.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Trash2 className="w-4 h-4" />}
                  <span>Delete</span>
                </button>
              )}
            </div>
          </div>

          {/* Reply Input */}
          {replyingTo === comment.id && (
            <div className="mt-3 ml-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Write a reply..."
                  disabled={isSubmitting}
                  className="article-comment-input flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:opacity-50"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmitReply(comment.id);
                    }
                  }}
                />
                <button
                  onClick={() => handleSubmitReply(comment.id)}
                  disabled={isSubmitting || !replyContent.trim()}
                  className="article-comment-reply-button px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Replies */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="mt-4">
              {comment.replies.map((reply) => renderComment(reply, true))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={`border-t border-gray-200 dark:border-gray-700 pt-8 mt-8 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading comments...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`border-t border-gray-200 dark:border-gray-700 pt-8 mt-8 ${className}`}>
      <div className="flex items-center gap-2 mb-6">
        <MessageSquare className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Comments ({comments.length})
        </h3>
      </div>

      {/* Comment Input */}
      {state.isAuthenticated ? (
        <div className="mb-8">
          <div className="flex gap-3 mb-4">
            <div className="flex-shrink-0">
              {state.user?.avatar ? (
                <img
                  src={state.user.avatar}
                  alt={state.user.fullName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="comment-author-avatar w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold text-sm">
                  {state.user?.fullName?.charAt(0) || 'U'}
                </div>
              )}
            </div>
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                rows={3}
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:opacity-50"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim() || isSubmitting}
                  className="comment-submit-button px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Posting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Post Comment</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            Please sign in to leave a comment
          </p>
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-6">
        {comments.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          comments.map((comment) => renderComment(comment))
        )}
      </div>

      {commentPendingDelete && (
        <div
          className="article-delete-dialog-backdrop"
          role="presentation"
          onMouseDown={() => !deletingCommentId && setCommentPendingDelete(null)}
        >
          <div
            className="article-delete-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-comment-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="article-delete-dialog__icon" aria-hidden="true">
              <Trash2 className="w-5 h-5" />
            </div>
            <div>
              <h4 id="delete-comment-title">Delete comment?</h4>
              <p>This comment will be permanently removed.</p>
            </div>
            <div className="article-delete-dialog__actions">
              <button
                type="button"
                className="article-delete-dialog__cancel"
                disabled={Boolean(deletingCommentId)}
                onClick={() => setCommentPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="article-delete-dialog__confirm"
                disabled={Boolean(deletingCommentId)}
                onClick={() => handleDeleteComment(commentPendingDelete)}
              >
                {deletingCommentId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                <span>{deletingCommentId ? 'Deleting…' : 'Delete'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentSection;
